import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import type { GameState, LogEntry, TabName } from '@/types';
import { GAME_CONFIG, getPersonnelRatio } from '@/config/gameConfig';
import { EntityManager } from '@/entities/entityManager';
import { 
  saveGameState, 
  loadGameState, 
  clearGameState,
  _hasInfrastructure,
} from '@/utils/gameUtils';
import { checkUpgradePurchase } from '@/types/UpgradeEntity';

// ============================================================================
// ERROR HANDLING TYPES
// ============================================================================

export class UpgradeError extends Error {
  constructor(
    public type: 'upgrade_effect_failed' | 'state_corruption' | 'insufficient_resources' | 'unknown',
    message: string,
    public upgradeId: string,
    public originalError?: unknown
  ) {
    super(message);
    this.name = 'UpgradeError';
  }
}

const {
  RAW_DATA_SELL_VALUE,
  CLEAN_DATA_SELL_VALUE,
  INSIGHTS_SELL_VALUE,
  FEATURES_SELL_VALUE,
  MODELS_SELL_VALUE,
  MIN_FAILURE_RATE,
  MAX_FAILURE_RATE,
  EVENT_PROBABILITY,
  GAME_LOOP_INTERVAL
} = GAME_CONFIG.CONSTANTS;

// ============================================================================
// GAME STORE
// ============================================================================

export const useGameStore = defineStore('game', () => {
  // ========================================================================
  // STATE
  // ========================================================================

  const state = ref<GameState>(createInitialState());
  const entityManager = new EntityManager();
  
  // UI State
  const activeTab = ref<TabName>('pipeline');
  const logs = ref<LogEntry[]>([]);
  const currentEvent = ref<string | null>(null);
  const showEndGameModal = ref(false);
  const endGameResult = ref<'won' | 'lost' | null>(null);
  
  // Game Loop State
  const gameRunning = ref(false);
  const gameLoopInterval = ref<number | null>(null);
  const saveInterval = ref<number | null>(null);

  // ========================================================================
  // COMPUTED PROPERTIES
  // ========================================================================

  const netProfitPerSec = computed(() => 
    (state.value.productionRates.money || 0) - (state.value.expenses.totalExpensesPerSec || 0)
  );

  const gameStats = computed(() => ({
    ...state.value,
    netProfitPerSec: netProfitPerSec.value,
    infrastructureReliability: infrastructureReliability.value
  }));

  const hasUpgrade = computed(() => (upgradeId: string) => 
    state.value.upgrades[upgradeId] || false
  );

  const availableUpgrades = computed(() =>
    entityManager.getAvailableUpgrades(state.value)
  );

  const availableDataUpgrades = computed(() =>
    entityManager.getAvailableUpgrades(state.value).filter(upgrade => upgrade.upgradeStage == "data")
  );

  const availableMLUpgrades = computed(() =>
    entityManager.getAvailableUpgrades(state.value).filter(upgrade => upgrade.upgradeStage == "ml")
  );

  const newAchievements = computed(() => 
    entityManager.getNewAchievements(state.value)
  );

  const infrastructureReliability = computed(() => {
    const reliability = (1 - state.value.infrastructureFailureRate) * 100;
    return Math.max(0, Math.min(100, reliability));
  });

  const hasInfrastructure = computed(() => _hasInfrastructure(state.value));

  const hasPersonnel = computed(() => {
      return (state.value.personnel.dataEngineer || 0) > 0 ||
           (state.value.personnel.mlEngineer || 0) > 0 ||
           (state.value.personnel.aiResearcher || 0) > 0 ||
           (state.value.personnel.reasoningSpecialist || 0) > 0;
  });

  // ========================================================================
  // ACTIONS
  // ========================================================================

  function reset() {
    state.value = createInitialState();
    logs.value = [];
    currentEvent.value = null;
    showEndGameModal.value = false;
    endGameResult.value = null;
  }

  function start() {
    if (gameRunning.value) return;

    // Calculate initial income, expenses, storage capacity, and production
    calculateExpenses();
    calculateStorageCapacity();
    calculateProductionRates();

    gameRunning.value = true;
    gameLoopInterval.value = window.setInterval(gameLoop, GAME_LOOP_INTERVAL);
    saveInterval.value = window.setInterval(save, 5000);

    log('Game started!', 'success');
  }

  function stop() {
    if (!gameRunning.value) return;
    
    gameRunning.value = false;
    
    if (gameLoopInterval.value) {
      clearInterval(gameLoopInterval.value);
      gameLoopInterval.value = null;
    }
    if (saveInterval.value) {
      clearInterval(saveInterval.value);
      saveInterval.value = null;
    }
    
    log('Game paused!', 'warning');
  }

  function restart() {
    stop();
    reset();
    clearGameState();
    start();
  }

  // ========================================================================
  // GAME ACTIONS
  // ========================================================================

  function collectRawData() {
    const currentRawData = state.value.resources.rawData || 0;
    const storageCapacity = state.value.storageCapacity.rawData || 0;

    if (currentRawData >= storageCapacity) {
      log('Raw data storage is full! Upgrade storage capacity.', 'warning');
      return;
    }

    state.value.resources.rawData = currentRawData + 1;
    state.value.lifetimeStats.totalRawDataCollected++;
    log('Collected raw data manually', 'success');
  }

  function sellRawData() {
    if ((state.value.resources.rawData || 0) >= 1) {
      state.value.resources.rawData = (state.value.resources.rawData || 0) - 1;
      state.value.resources.money = (state.value.resources.money || 0) + RAW_DATA_SELL_VALUE;
      log(`Sold raw data for $${RAW_DATA_SELL_VALUE}`, 'success');
    } else {
      log('No raw data to sell!', 'warning');
    }
  }

  function cleanData() {
    if (!hasUpgrade.value('dataCleaning')) {
      log('Data cleaning not available yet! Buy the upgrade first.', 'warning');
      return;
    }

    const currentCleanData = state.value.resources.cleanData || 0;
    const storageCapacity = state.value.storageCapacity.cleanData || 0;

    if (currentCleanData >= storageCapacity) {
      log('Clean data storage is full! Upgrade storage capacity.', 'warning');
      return;
    }

    if ((state.value.resources.rawData || 0) >= 1) {
      state.value.resources.rawData = (state.value.resources.rawData || 0) - 1;
      state.value.resources.cleanData = (state.value.resources.cleanData || 0) + 1;
      state.value.lifetimeStats.totalCleanDataProduced++;
      log('Cleaned 1 raw data point', 'info');
    }
  }

  function sellCleanData() {
    if (!hasUpgrade.value('dataCleaning')) {
      log('Clean data selling not available yet! Buy the upgrade first.', 'warning');
      return;
    }
    if ((state.value.resources.cleanData || 0) >= 1) {
      state.value.resources.cleanData = (state.value.resources.cleanData || 0) - 1;
      state.value.resources.money = (state.value.resources.money || 0) + CLEAN_DATA_SELL_VALUE;
      log(`Sold clean data for $${CLEAN_DATA_SELL_VALUE}`, 'success');
    } else {
      log('No clean data to sell!', 'warning');
    }
  }

  function buyInfrastructure(infrastructureId: string) {
    const infrastructure = entityManager.getInfrastructureById(infrastructureId);
    if (!infrastructure) return;
    
    const validationResult = validateInfrastructurePurchase(infrastructure, state.value);
    if (!validationResult.canPurchase) {
      log(validationResult.reason, validationResult.logType);
      return;
    }
    
    executeInfrastructurePurchase(infrastructure, state.value);
  }

  function validateInfrastructurePurchase(infrastructure: any, gameState: GameState): { canPurchase: boolean; reason: string; logType: 'warning' | 'error' } {
    // Check max count limit
    if (infrastructure.maxCount !== undefined) {
      const propertyName = infrastructure.getStatePropertyName() as keyof GameState['infrastructure'];
      const currentCount = (gameState.infrastructure[propertyName] as number) || 0;
      if (currentCount >= infrastructure.maxCount) {
        return {
          canPurchase: false,
          reason: `Maximum ${infrastructure.name} limit reached (${infrastructure.maxCount} max)`,
          logType: 'warning'
        };
      }
    }

    if (!infrastructure.canBeBuilt(gameState)) {
      if (infrastructure.progressionRequirements?.personnel) {
        const requirements = buildPersonnelRequirements(infrastructure, gameState);
        const requiredUpgrades = infrastructure.progressionRequirements?.upgrades?.join(', ') || 'required upgrade';
        return {
          canPurchase: false,
          reason: `${infrastructure.name} requires: ${requirements.join(', ')} and the ${requiredUpgrades}!`,
          logType: 'warning'
        };
      } else {
        return {
          canPurchase: false,
          reason: `${infrastructure.name} not available yet! Buy the required upgrade first.`,
          logType: 'warning'
        };
      }
    }

    if (!infrastructure.canAfford((gameState.resources.money || 0))) {
      return {
        canPurchase: false,
        reason: 'Not enough money!',
        logType: 'error'
      };
    }

    return { canPurchase: true, reason: '', logType: 'warning' };
  }

  function buildPersonnelRequirements(infrastructure: any, gameState: GameState): string[] {
    const requirements: string[] = [];
    const personnelReqs = infrastructure.progressionRequirements.personnel;

    if (!personnelReqs) return requirements;

    // Personnel types that may have ratio-based requirements
    const ratioBasedPersonnel: Record<string, { ids: string[], label: string }> = {
      dataEngineer: {
        ids: ['etlPipeline', 'streamingSystem'],
        label: 'Data Engineer(s)'
      },
      mlEngineer: {
        ids: ['featurePipeline'],
        label: 'ML Engineer(s)'
      },
      aiResearcher: {
        ids: ['trainingPipeline'],
        label: 'AI Researcher(s)'
      }
    };

    // Build requirements for ratio-based personnel
    for (const [personnelType, config] of Object.entries(ratioBasedPersonnel)) {
      if (personnelReqs[personnelType]) {
        const req = buildRatioBasedRequirement(
          infrastructure,
          gameState,
          personnelType as 'dataEngineer' | 'mlEngineer' | 'aiResearcher' | 'reasoningSpecialist',
          config.ids,
          config.label
        );
        if (req) requirements.push(req);
      }
    }

    // Handle simple count-based personnel requirements
    if (personnelReqs.scientist) {
      requirements.push(`${personnelReqs.scientist} Data Scientist(s)`);
    }
    if (personnelReqs.reasoningSpecialist) {
      requirements.push(`${personnelReqs.reasoningSpecialist} Reasoning Specialist(s)`);
    }

    return requirements;
  }

  function buildRatioBasedRequirement(
    infrastructure: any,
    gameState: GameState,
    personnelType: 'dataEngineer' | 'mlEngineer' | 'aiResearcher' | 'reasoningSpecialist',
    infrastructureIds: string[],
    label: string
  ): string | null {
    // Check if this infrastructure has ratio-based requirements
    for (const infraId of infrastructureIds) {
      if (infrastructure.id === infraId) {
        const ratio = getPersonnelRatio(personnelType, infraId, gameState);
        const currentCount = gameState.infrastructure[infraId as keyof typeof gameState.infrastructure] || 0;
        const required = Math.ceil((currentCount + 1) / ratio);
        const infraName = infraId.replace(/([A-Z])/g, ' $1').trim().toUpperCase();
        return `${required} ${label} (can manage ${ratio} ${infraName}${ratio > 1 ? 's' : ''} each)`;
      }
    }

    // Fall back to simple count requirement
    const count = infrastructure.progressionRequirements.personnel[personnelType];
    return count ? `${count} ${label}` : null;
  }

  function executeInfrastructurePurchase(infrastructure: any, gameState: GameState): void {
    gameState.resources.money = (gameState.resources.money || 0) - infrastructure.cost;
    const propertyName = infrastructure.getStatePropertyName() as keyof GameState['infrastructure'];
    (gameState.infrastructure[propertyName] as number) = (gameState.infrastructure[propertyName] as number || 0) + 1;

    // Track lifetime stats
    gameState.lifetimeStats.totalInfrastructureBuilt++;

    // Recalculate all rates and capacities from current state
    calculateAllRates();

    log(`Built ${infrastructure.name}!`, 'success');
  }

  function hirePerson(personId: string, stateProperty: keyof GameState['personnel']) {
    const person = entityManager.getPersonById(personId);

    if (!person) return;

    if (!person.canBeBuilt(state.value)) {
      log(`${person.name} hiring not available yet! Buy the personnel upgrade first.`, 'warning');
      return;
    }
    if (!person.canAfford((state.value.resources.money || 0))) {
      log('Not enough money!', 'error');
      return;
    }

    state.value.resources.money = (state.value.resources.money || 0) - person.cost;
    (state.value.personnel[stateProperty] as number)++;

    // Track lifetime stats
    state.value.lifetimeStats.totalPersonnelHired++;

    log(`Hired ${person.name}!`, 'success');
  }

  function buyUpgrade(upgradeId: string) {
    const upgrade = entityManager.getUpgradeById(upgradeId);
    
    if (!upgrade) {
      log('Upgrade not found!', 'error');
      return;
    }
    
    const purchaseCheck = checkUpgradePurchase(upgrade, state.value);
    if (!purchaseCheck.allowed) {
      log(`Cannot purchase ${upgrade.name}. ${purchaseCheck.reason || 'Requirements not met.'}`, 'warning');
      return;
    }
    
    const purchaseResult = executeUpgradePurchase(upgrade, state.value);
    if (!purchaseResult.success && purchaseResult.error) {
      handleUpgradeError(purchaseResult.error, upgrade, state.value);
      return;
    }
    
    log(`Upgraded to ${upgrade.name}!`, 'success');
  }

  function executeUpgradePurchase(upgrade: any, gameState: GameState): { success: boolean; error?: UpgradeError } {
    // Check if this is a research upgrade
    const isResearchUpgrade = upgrade.progressionRequirements?.resources?.researchProgress !== undefined;
    const researchCost = upgrade.progressionRequirements?.resources?.researchProgress || 0;

    // Deduct cost first (either money or research progress)
    if (isResearchUpgrade) {
      gameState.resources.researchProgress = (gameState.resources.researchProgress || 0) - researchCost;
    } else {
      gameState.resources.money = (gameState.resources.money || 0) - upgrade.cost;
    }

    try {
      // Apply upgrade effect
      upgrade.applyEffect(gameState);

      // If successful, mark as purchased
      gameState.upgrades[upgrade.id] = true;

      // Recalculate storage capacity if upgrade affects it
      calculateStorageCapacity();

      return { success: true };
    } catch (error) {
      // Refund on failure
      if (isResearchUpgrade) {
        gameState.resources.researchProgress = (gameState.resources.researchProgress || 0) + researchCost;
      } else {
        gameState.resources.money = (gameState.resources.money || 0) + upgrade.cost;
      }

      return {
        success: false,
        error: new UpgradeError(
          'upgrade_effect_failed',
          error instanceof Error ? error.message : 'Unknown upgrade error',
          upgrade.id,
          error
        )
      };
    }
  }

  function handleUpgradeError(error: UpgradeError, upgrade: any, _gameState: GameState): void {
    const errorMessage = getUpgradeErrorMessage(error, upgrade);
    log(errorMessage, 'error');
    
    // Report to error tracking service (in a real application)
    reportUpgradeError(error, upgrade);
    
    // Additional recovery actions based on error type
    switch (error.type) {
      case 'upgrade_effect_failed':
        // Already refunded money in executeUpgradePurchase
        log('Money has been refunded due to upgrade failure.', 'info');
        break;
      case 'state_corruption':
        // Could implement state recovery here
        log('Game state may need to be reloaded.', 'warning');
        break;
      default:
        log('An unexpected error occurred during upgrade.', 'error');
    }
  }

  function getUpgradeErrorMessage(error: UpgradeError, _upgrade: any): string {
    switch (error.type) {
      case 'upgrade_effect_failed':
        return `Upgrade failed: ${error.message}`;
      case 'state_corruption':
        return `Upgrade caused state corruption: ${error.message}`;
      case 'insufficient_resources':
        return `Upgrade requires more resources than available: ${error.message}`;
      default:
        return `Upgrade error: ${error.message}`;
    }
  }

  function reportUpgradeError(error: UpgradeError, upgrade: any): void {
    // In a real application, this would send to error tracking service
    console.error('Upgrade Error Report:', {
      upgradeId: upgrade.id,
      upgradeName: upgrade.name,
      errorType: error.type,
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
      stack: error.originalError instanceof Error ? error.originalError.stack : undefined
    });
  }

  // ========================================================================
  // GAME LOOP
  // ========================================================================

  function gameLoop() {
    if (!gameRunning.value) return;

    const timeStep = GAME_LOOP_INTERVAL / 1000; // Convert milliseconds to seconds

    // Update all game systems
    updateGameSystems(timeStep);
    
    // Check win/lose conditions
    checkGameEndConditions();
    
    // Update UI elements
    checkAchievements();
    triggerRandomEvent();

  }

  // ========================================================================
  // SIMPLIFIED CALCULATIONS
  // ========================================================================

  function updateGameSystems(timeStep: number) {
    // Calculate all rates and capacities
    calculateAllRates();
    
    // Apply production and consumption
    applyResourceChanges(timeStep);
    
    // Update money
    updateMoney(timeStep);
    
    // Check for failures
    checkFailures();
  }

  function calculateAllRates() {
    calculateExpenses();
    calculateStorageCapacity();
    calculateInfrastructureFailureRate();
    calculateProductionRates();
  }

  function applyResourceChanges(timeStep: number) {
    // Track resources before production
    const beforeRawData = state.value.resources.rawData || 0;
    const beforeCleanData = state.value.resources.cleanData || 0;
    const beforeFeature = state.value.resources.feature || 0;
    const beforeModel = state.value.resources.model || 0;
    const beforeReasoningEngine = state.value.resources.reasoningEngine || 0;

    entityManager.applyEntityProductionAndConsumption(state.value, timeStep);

    // Track lifetime stats for automated production
    const rawDataProduced = Math.max(0, (state.value.resources.rawData || 0) - beforeRawData);
    const cleanDataProduced = Math.max(0, (state.value.resources.cleanData || 0) - beforeCleanData);
    const featuresProduced = Math.max(0, (state.value.resources.feature || 0) - beforeFeature);
    const modelsProduced = Math.max(0, (state.value.resources.model || 0) - beforeModel);
    const reasoningEnginesProduced = Math.max(0, (state.value.resources.reasoningEngine || 0) - beforeReasoningEngine);

    state.value.lifetimeStats.totalRawDataCollected += rawDataProduced;
    state.value.lifetimeStats.totalCleanDataProduced += cleanDataProduced;
    state.value.lifetimeStats.totalFeaturesEngineered += featuresProduced;
    state.value.lifetimeStats.totalModelsBuilt += modelsProduced;
    state.value.lifetimeStats.totalReasoningEnginesBuilt += reasoningEnginesProduced;

    // Process AGI progress
    processAGIProgress();
  }

  function updateMoney(timeStep: number) {
    const netProfit = (state.value.productionRates.money || 0) - (state.value.expenses.totalExpensesPerSec || 0);
    state.value.resources.money = (state.value.resources.money || 0) + netProfit * timeStep;

    // Track max money
    if (state.value.resources.money > state.value.lifetimeStats.maxMoney) {
      state.value.lifetimeStats.maxMoney = state.value.resources.money;
    }

    // Check for negative balance
    if (state.value.resources.money < 0) {
      state.value.consecutiveNegativeBalance++;
      log(`⚠️ Negative balance! Consecutive negative: ${state.value.consecutiveNegativeBalance}/${state.value.maxConsecutiveNegativeBalance}`, 'warning');
    } else {
      state.value.consecutiveNegativeBalance = 0;
    }
  }

  function checkFailures() {
    // Check personnel leaving
    const personnel = entityManager.persons.filter(person => {
      const count = state.value[person.getStatePropertyName() as keyof GameState] as number;
      return count > 0 && person.shouldLeave(state.value);
    });
    
    if (personnel.length > 0) {
      const leaving = personnel[Math.floor(Math.random() * personnel.length)];
      leaving.applyLeaving(state.value, leaving.getStatePropertyName());
      log(leaving.getLeavingMessage(), 'warning');
    }
    
    // Check infrastructure degradation
    const infrastructure = entityManager.infrastructure.filter(inf => {
      const count = state.value[inf.getStatePropertyName() as keyof GameState] as number;
      return count > 0 && inf.shouldDegrade(state.value);
    });
    
    if (infrastructure.length > 0) {
      const degrading = infrastructure[Math.floor(Math.random() * infrastructure.length)];
      degrading.applyDegradation(state.value, degrading.getStatePropertyName() as keyof GameState);
      log(degrading.getDegradationMessage(), 'warning');
    }
  }

  function checkGameEndConditions() {
    // Check for bankruptcy
    if (state.value.consecutiveNegativeBalance >= state.value.maxConsecutiveNegativeBalance) {
      log('💥 GAME OVER! Your data empire went bankrupt!', 'error');
      state.value.gameStatus = 'lost';
      state.value.gameEndTime = Date.now();
      stop();
      showEndGameModal.value = true;
      endGameResult.value = 'lost';
      return;
    }
    
    // Check for AGI victory
    if (state.value.agiProgress >= 100) {
      log('🎉 VICTORY! AGI has been achieved! Humanity enters a new era!', 'success');
      state.value.gameStatus = 'won';
      state.value.gameEndTime = Date.now();
      stop();
      showEndGameModal.value = true;
      endGameResult.value = 'won';
      return;
    }
  }

  function processAGIProgress() {
      state.value.agiProgress += state.value.resources.agiScore || 0;
  }

  // ========================================================================
  // CALCULATIONS
  // ========================================================================

  function calculateExpenses() {
    const expenses = entityManager.calculateTotalExpenses(state.value);
    Object.assign(state.value.expenses, expenses);
  }

  function calculateInfrastructureFailureRate() {
    state.value.infrastructureFailureRate = entityManager.calculateInfrastructureFailureRate(state.value);
  }

  function calculateProductionRates() {
    const production = entityManager.calculateTotalProduction(state.value);
    Object.assign(state.value.productionRates, production);
  }

  function calculateStorageCapacity() {
    const storage = entityManager.calculateTotalStorageCapacity(state.value);
    Object.assign(state.value.storageCapacity, storage);
  }

  // ========================================================================
  // ACHIEVEMENTS AND EVENTS
  // ========================================================================

  function checkAchievements() {
    const newAchievements = entityManager.getNewAchievements(state.value);
    newAchievements.forEach(achievement => {
      state.value.achievements.push(achievement.id);
      log(`🏆 Achievement Unlocked: ${achievement.name}!`, 'success');
    });
  }

  function triggerRandomEvent() {
    if (Math.random() < EVENT_PROBABILITY) {
      const possibleEvents = entityManager.getPossibleEvents(state.value);
      if (possibleEvents.length > 0) {
        const event = possibleEvents[Math.floor(Math.random() * possibleEvents.length)];
        event.execute({ state: state.value, log });
        log(`Event: ${event.name}`, 'warning');
        currentEvent.value = event.getDisplayText();
        
        // Clear event after 8 seconds
        setTimeout(() => {
          currentEvent.value = null;
        }, 8000);
      }
    }
  }

  // ========================================================================
  // UTILITY FUNCTIONS
  // ========================================================================

  function log(message: string, type: 'info' | 'success' | 'warning' | 'error' = 'info') {
    const timestamp = Date.now();
    const logEntry: LogEntry = {
      id: `${timestamp}-${Math.random().toString(36).substr(2, 9)}`,
      message,
      type,
      timestamp
    };
    
    logs.value.push(logEntry);
    
    // Keep only the last 100 log entries
    if (logs.value.length > 100) {
      logs.value = logs.value.slice(-100);
    }
  }

  function save() {
    saveGameState(state.value);
  }

  function load() {
    const saved = loadGameState();
    if (saved) {
      state.value = saved;
      // Recalculate income, expenses, and storage capacity from current state
      calculateAllRates();
      log('Game loaded from save', 'info');
    }
  }

  function setActiveTab(tab: TabName) {
    activeTab.value = tab;
  }

  function closeEndGameModal() {
    showEndGameModal.value = false;
    endGameResult.value = null;
  }

  // ========================================================================
  // HELPER FUNCTIONS
  // ========================================================================

  function createInitialState(): GameState {
    return {
      // Core resources
      resources: {
        money: 0,
        rawData: 0,
        cleanData: 0,
        feature: 0,
        model: 0,
        reasoningEngine: 0,
        researchProgress: 0
      },

      // Storage capacity
      storageCapacity: {
        rawData: 50,
        cleanData: 50,
        feature: 25,
        model: 1,
        reasoningEngine: 1,
        researchProgress: 100
      },

      // Production rates (per second)
      productionRates: {
        money: 0,
        rawData: 0,
        cleanData: 0,
        feature: 0,
        model: 0,
        reasoningEngine: 0,
        researchProgress: 0
      },
      
      // Infrastructure reliability
      infrastructureFailureRate: MIN_FAILURE_RATE,
      failureRateMultiplier: 1,

      // Storage capacity multipliers
      storageCapacityMultipliers: {
        rawData: 1,
        cleanData: 1,
        feature: 1,
        model: 1,
        reasoningEngine: 1,
        researchProgress: 1
      },

      // Data governance and security level
      securityLevel: 0,
      securityMultiplier: 1,
      
      // Infrastructure components
      infrastructure: {
        webScraper: 0,
        database: 0,
        cleanDataApi: 0,
        iotSensor: 0,
        dataCleaner: 0,
        etlPipeline: 0,
        streamingSystem: 0,
        featureStore: 0,
        featurePipeline: 0,
        trainingPipeline: 0,
        modelRegistry: 0,
        modelApi: 0,
        agiCluster: 0
      },
      
      // Personnel
      personnel: {
        dataEngineer: 0,
        mlEngineer: 0,
        aiResearcher: 0,
        reasoningSpecialist: 0,
        agiSafetyResearcher: 0,
      },
      
      // Expenses (per second)
      expenses: {
        talentExpensesPerSec: 0,
        infrastructureExpensesPerSec: 0,
        totalExpensesPerSec: 0
      },
      
      // Game progression
      upgrades: {},
      achievements: [],
      lastSave: Date.now(),

      // Game state tracking
      gameStartTime: Date.now(),
      gameEndTime: null,
      gameStatus: 'playing',
      consecutiveNegativeBalance: 0,
      maxConsecutiveNegativeBalance: 10,
      
      // AGI progress tracking
      agiProgress: 0,

      // Lifetime statistics
      lifetimeStats: {
        maxMoney: 0,
        totalRawDataCollected: 0,
        totalCleanDataProduced: 0,
        totalFeaturesEngineered: 0,
        totalModelsBuilt: 0,
        totalReasoningEnginesBuilt: 0,
        totalInfrastructureBuilt: 0,
        totalPersonnelHired: 0
      }
    };
  }



  // ========================================================================
  // WATCHERS
  // ========================================================================

  // Auto-save when state changes
  watch(state, () => {
    if (gameRunning.value) {
      save();
    }
  }, { deep: true });

  // ========================================================================
  // RETURN
  // ========================================================================

  return {
    // State
    state,
    activeTab,
    logs,
    currentEvent,
    showEndGameModal,
    endGameResult,
    gameRunning,
    
    // Computed
    gameStats,
    hasUpgrade,
    availableUpgrades,
    availableDataUpgrades,
    availableMLUpgrades,
    newAchievements,
    infrastructureReliability,
    hasInfrastructure,
    hasPersonnel,
    netProfitPerSec,
    
    // Constants
    RAW_DATA_SELL_VALUE,
    CLEAN_DATA_SELL_VALUE,
    INSIGHTS_SELL_VALUE,
    FEATURES_SELL_VALUE,
    MODELS_SELL_VALUE,
    MIN_FAILURE_RATE,
    MAX_FAILURE_RATE,
    
    // Actions
    reset,
    start,
    stop,
    restart,
    collectRawData,
    sellRawData,
    cleanData,
    sellCleanData,

    buyInfrastructure,
    hirePerson,
    buyUpgrade,
    log,
    load,
    setActiveTab,
    closeEndGameModal,
    
    // Entity Manager
    entityManager
  };
}); 